import React from "react";

export const HIGHLIGHT_COLOR = "rgb(255, 217, 0)";

/**
 * Highlights matching text within a string by wrapping matches in a styled span.
 *
 * @param text - The text to search within
 * @param searchTerm - The term to highlight (case-insensitive)
 * @param highlightStyle - Optional custom styles for highlighted text
 * @returns React node with highlighted matches
 *
 * @example
 * highlightText("Hello World", "wor")
 * // Returns: "Hello <span style={{...}}>Wor</span>ld"
 */
export function highlightText(text: string, searchTerm: string, highlightStyle?: React.CSSProperties): React.ReactNode {
    if (!searchTerm.trim()) {
        return text;
    }

    // Escape special regex characters to treat search term as literal text
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedSearchTerm})`, "gi");

    // If no matches found, return original text
    if (!regex.test(text)) {
        return text;
    }

    // Reset regex for splitting (test() moves the lastIndex)
    regex.lastIndex = 0;
    const parts = text.split(regex);

    const defaultStyle: React.CSSProperties = {
        backgroundColor: HIGHLIGHT_COLOR,
        fontWeight: "bold",
    };

    const appliedStyle = { ...defaultStyle, ...highlightStyle };
    const searchLower = searchTerm.toLowerCase();

    return parts.map((part, index) => {
        // Use simple string comparison instead of regex test
        if (part.toLowerCase() === searchLower) {
            return (
                <span key={index} style={appliedStyle}>
                    {part}
                </span>
            );
        }
        return part;
    });
}
